import { useEffect, useRef, useState } from "react";
import { useCustomerChatStore } from "../../../store/customerChatStore";
import { useAuth } from "../../../context/AuthContext";
import { decodeTokenService } from "../../../services/decodeTokenService";
import { dictionary } from "../../../utils/dictionary";
import { Phone, Bot, KanbanSquare, ExternalLink, Send, CheckCircle, XCircle, UserCircle, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import MessageBubble from "./MessageBubble";
import OrderIndicator from "./OrderIndicator";
import { ref, onValue, off } from "firebase/database";
import { database } from "../../../lib/firebase";
import config from "../../../config/environment";
import { getRealtimeConfig } from "../../../services/whatsapp/whatsappService";
import { handleApi } from "../../../utils/handleApi";

const AIToggleSwitch = ({
  enabled,
  onToggle,
  disabled,
}: {
  enabled: boolean;
  onToggle: () => void;
  disabled: boolean;
}) => (
  <div
    className={`flex items-center gap-2 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    onClick={disabled ? undefined : onToggle}
    title={enabled ? "IA activada" : "IA desactivada"}
  >
    <Bot className="w-6 h-6 text-gray-500" />
    <span className="text-xs text-gray-500 font-medium">IA</span>
    <div className={`w-10 h-5 rounded-full transition-colors ${enabled ? "bg-[#00A884]" : "bg-gray-300"}`}>
      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform mt-[2px] ${enabled ? "translate-x-5" : "translate-x-[2px]"}`} />
    </div>
  </div>
);

interface Props {
  onOpenContactPanel: () => void;
}

const UnifiedChatDetail = ({ onOpenContactPanel }: Props) => {
  const { language } = useAuth();
  const {
    selectedPhone,
    customerConversation: conv,
    conversationLoading,
    customers,
    toggleAI,
    sendMessage,
    confirmOrder,
    cancelOrder,
    updateMessagesFromFirebase,
  } = useCustomerChatStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [toggling, setToggling] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  // The wa-service uses its own ENV as the Firebase path prefix when it pushes
  // updates. If we relied on VITE_ENV alone and it drifted from the backend's
  // value (eg "dev" vs "development"), paths wouldn't match and real-time
  // updates would never arrive — so we ask the backend for its authoritative
  // value on mount.
  const [fbEnv, setFbEnv] = useState<string | null>(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [conv?.messages, conversationLoading]);

  // Resolve the backend's Firebase env prefix once on mount; fall back to
  // VITE_ENV if the endpoint isn't deployed yet.
  useEffect(() => {
    let cancelled = false;
    handleApi(getRealtimeConfig(), false).then(({ res }) => {
      if (cancelled) return;
      const resolved = res?.env || config.environment || "development";
      setFbEnv(resolved);
      // eslint-disable-next-line no-console
      console.log("[chat-realtime] backend ENV =", resolved, "(VITE_ENV =", config.environment, ")");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Firebase real-time listeners for all orders of this customer
  useEffect(() => {
    if (!conv?.orders || !fbEnv) return;
    const refs: ReturnType<typeof ref>[] = [];

    conv.orders.forEach((order) => {
      const path = `${fbEnv}/ws-confirmations/${order.confirmation_id}`;
      const fbRef = ref(database, path);
      // eslint-disable-next-line no-console
      console.log("[chat-realtime] listening on", path);
      onValue(fbRef, (snapshot) => {
        const data = snapshot.val();
        // eslint-disable-next-line no-console
        console.log("[chat-realtime] snapshot", path, data);
        if (data && data.messages) {
          updateMessagesFromFirebase(data, order.confirmation_id);
        }
      });
      refs.push(fbRef);
    });

    return () => refs.forEach((r) => off(r));
  }, [conv?.orders?.map((o) => o.confirmation_id).join(","), fbEnv]);

  if (!selectedPhone || !conv) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#F0F2F5]">
        <p className="text-[#667781] text-sm">
          {dictionary["chat-select-conversation"]?.[language] || "Selecciona una conversación"}
        </p>
      </div>
    );
  }

  // Header name must match the conversation list (left column). The unified
  // messages endpoint returns `customer_name` from whichever order it picked,
  // which isn't always the same one that appears in /admin/customers used by
  // the list. Fall back to conv.customer_name → phone only if no match.
  const customerFromList = customers.find((c) => c.customer_phone === selectedPhone);
  const displayName =
    customerFromList?.display_name ||
    customerFromList?.customer_name ||
    conv.customer_name ||
    selectedPhone;

  // While the new conversation is loading, `conv` still holds the previous
  // chat's data — using it would flash the wrong AI toggle position and
  // re-render Confirmar/Cancelar based on stale order status, which the user
  // sees as a "weird slide" in the header. Treat the conv as stale until the
  // fetch resolves AND its phone matches the selected one.
  const isStale =
    conversationLoading || conv.customer_phone !== selectedPhone;

  const activeOrder = conv.orders.find((o) => o.confirmation_id === conv.active_confirmation_id);
  const aiEnabled = !isStale && (activeOrder?.ai_enabled ?? false);
  const canConfirm =
    !isStale && activeOrder && ["awaiting_response", "pending", "expired"].includes(activeOrder.status);
  const canCancel =
    !isStale && activeOrder && ["awaiting_response", "pending", "expired", "confirmed"].includes(activeOrder.status);

  const handleToggleAI = async () => {
    if (!activeOrder) return;
    setToggling(true);
    try {
      await toggleAI(activeOrder.confirmation_id, !aiEnabled);
    } catch {
      toast.error("Error al cambiar el modo de IA");
    } finally {
      setToggling(false);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim() || !activeOrder) return;
    if (aiEnabled) {
      toast.error(dictionary["ai-active-warning"]?.[language]);
      return;
    }
    const text = inputValue.trim();
    setInputValue("");
    sendMessage(activeOrder.confirmation_id, text).catch((err) => {
      const status = (err as any)?.response?.status;
      const detail = (err as any)?.response?.data?.detail;
      if (status === 409 && detail?.code === "outside_24h_window") {
        toast.error(
          dictionary["chat-window-closed-banner"]?.[language] ||
            detail?.message ||
            "Ventana de 24h cerrada. Envia una plantilla."
        );
      }
    });
  };

  const handleConfirmOrder = async () => {
    if (!activeOrder) return;
    if (aiEnabled) {
      toast.error(dictionary["ai-active-warning"]?.[language]);
      return;
    }
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;
    setConfirmingOrder(true);
    try {
      await confirmOrder(activeOrder.confirmation_id, ownerId);
      toast.success(dictionary["order-confirmed-success"]?.[language]);
    } catch {
      toast.error("Error al confirmar");
    } finally {
      setConfirmingOrder(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!activeOrder) return;
    if (aiEnabled) {
      toast.error(dictionary["ai-active-warning"]?.[language]);
      return;
    }
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;
    setCancellingOrder(true);
    setShowCancelDialog(false);
    try {
      await cancelOrder(activeOrder.confirmation_id, ownerId);
      toast.success(dictionary["order-canceled-success"]?.[language]);
    } catch {
      toast.error("Error al cancelar");
    } finally {
      setCancellingOrder(false);
    }
  };

  const openKanban = () => {
    window.open(`/chat/kanban/${selectedPhone}`, "_blank");
  };

  // Render messages with order separators
  const renderMessages = () => {
    let lastOrderId: number | null = null;
    const elements: JSX.Element[] = [];

    conv.messages.forEach((msg, i) => {
      if (msg.order_id !== lastOrderId) {
        const order = conv.orders.find((o) => o.order_id === msg.order_id);
        elements.push(
          <OrderIndicator
            key={`sep-${msg.order_id}-${i}`}
            orderId={msg.order_id}
            date={order?.created_at || undefined}
          />
        );
        lastOrderId = msg.order_id;
      }
      elements.push(
        <MessageBubble
          key={`msg-${i}`}
          message={msg}
        />
      );
    });

    return elements;
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[#5050EA] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {displayName?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-sm truncate">{displayName}</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Phone className="w-[18px] h-[18px]" />
              <span>{selectedPhone}</span>
              {!isStale && (
                <>
                  <span className="text-gray-300">|</span>
                  <span>
                    {conv.orders.length} {conv.orders.length === 1 ? "pedido" : "pedidos"}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isStale ? (
            <div className="flex items-center justify-center px-2">
              <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
            </div>
          ) : (
            <>
              <AIToggleSwitch enabled={aiEnabled} onToggle={handleToggleAI} disabled={toggling} />
              {canConfirm && (
                <button
                  onClick={handleConfirmOrder}
                  disabled={confirmingOrder || cancellingOrder}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-full transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-5 h-5" />
                  {dictionary["confirm-order"]?.[language] || "Confirmar"}
                </button>
              )}
              {canCancel && (
                <button
                  onClick={() => setShowCancelDialog(true)}
                  disabled={confirmingOrder || cancellingOrder}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-5 h-5" />
                  {dictionary["cancel-order"]?.[language] || "Cancelar"}
                </button>
              )}
            </>
          )}
          {/* Static buttons — these never depend on conv data so they can stay mounted while loading */}
          <button
            onClick={onOpenContactPanel}
            className="text-gray-400 hover:text-[#5050EA] transition-colors p-0.5"
            title={dictionary["contact-panel-open"]?.[language] ?? "Ver contacto"}
          >
            <UserCircle className="w-6 h-6" />
          </button>
          <button onClick={openKanban} className="text-gray-400 hover:text-[#5050EA] transition-colors p-0.5" title="Ver pedidos">
            <KanbanSquare className="w-6 h-6" />
          </button>
          {!isStale && activeOrder && (
            <a
              href={`/order/${activeOrder.display_code || activeOrder.order_hash_id || ""}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#5050EA] transition-colors p-0.5"
              title="Ver orden"
            >
              <ExternalLink className="w-6 h-6" />
            </a>
          )}
        </div>
      </div>

      {/* Cancel dialog */}
      {showCancelDialog && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center justify-between">
          <span className="text-xs text-red-700">
            {dictionary["chat-cancel-confirmation"]?.[language] || "¿Estás seguro de cancelar este pedido?"}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setShowCancelDialog(false)} className="px-3 py-1.5 text-xs text-gray-600 bg-white hover:bg-gray-50 rounded-md">
              No
            </button>
            <button onClick={handleCancelOrder} className="px-3 py-1.5 text-xs text-white bg-red-500 hover:bg-red-600 rounded-md">
              {dictionary["cancel-order"]?.[language] || "Cancelar"}
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto bg-[#EFEAE2] px-4 py-2 space-y-1.5">
        {conversationLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-[#00A884] rounded-full animate-spin" />
          </div>
        ) : conv.messages.length === 0 ? (
          <div className="text-center text-gray-500 py-12 text-xs">
            {dictionary["chat-no-messages"]?.[language] || "No hay mensajes aún"}
          </div>
        ) : (
          renderMessages()
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 bg-white border-t border-gray-200">
        {aiEnabled ? (
          <div className="text-center text-xs text-gray-400 py-2">
            <Bot className="w-4 h-4 inline mr-1" />
            {dictionary["chat-ai-responding"]?.[language] || "Las respuestas son gestionadas automáticamente por el agente de IA"}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              placeholder={dictionary["chat-input-placeholder"]?.[language] || "Escribe un mensaje..."}
              className="flex-1 text-sm border border-gray-200 rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-[#00A884]/40 focus:border-[#00A884]"
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
              className="w-9 h-9 rounded-full bg-[#00A884] text-white flex items-center justify-center hover:bg-[#009175] transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default UnifiedChatDetail;
